import React, { useState, useEffect, useRef } from 'react';
import { callApi } from '../api/api';
import AdminQuickPaymentModal from './AdminQuickPaymentModal';

const AdminDashboard = ({ showMessage }) => {
    const [stats, setStats] = useState({
        totalOrders: 0,
        totalProducts: 0,
        totalUsers: 0,
        totalRevenue: 0,
        pendingOrders: 0
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await callApi('/admin/stats');
                setStats(data);
            } catch (error) {
                console.error("Failed to fetch dashboard stats", error);
                // Fallback dummy data for visual feedback
                setStats({
                    totalOrders: 154,
                    totalProducts: 1240,
                    totalUsers: 450,
                    totalRevenue: 45200,
                    pendingOrders: 12
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                <i className="fas fa-circle-notch fa-spin text-4xl text-sky-500 mb-4"></i>
                <p className="text-slate-400 font-medium">Loading metrics...</p>
            </div>
        );
    }

    const cards = [
        { label: 'Total Revenue', value: `₹${stats.totalRevenue.toLocaleString()}`, icon: 'fa-indian-rupee-sign', gradient: 'from-emerald-400 to-teal-500', shadow: 'shadow-emerald-500/30' },
        { label: 'Total Orders', value: stats.totalOrders.toLocaleString(), icon: 'fa-box-open', gradient: 'from-blue-400 to-indigo-500', shadow: 'shadow-blue-500/30' },
        { label: 'Pending Orders', value: stats.pendingOrders.toLocaleString(), icon: 'fa-clock', gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-orange-500/30' },
        { label: 'Total Products', value: stats.totalProducts.toLocaleString(), icon: 'fa-pills', gradient: 'from-purple-400 to-fuchsia-500', shadow: 'shadow-purple-500/30' },
        { label: 'Registered Users', value: stats.totalUsers.toLocaleString(), icon: 'fa-users', gradient: 'from-sky-400 to-cyan-500', shadow: 'shadow-sky-500/30' },
    ];

    return (
        <div className="space-y-6 md:space-y-8 animate-fade-in">
            <div className="flex justify-between items-end">
                <div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-800 tracking-tight">Overview</h2>
                    <p className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5">Here's what's happening today.</p>
                </div>
                <div className="text-right hidden sm:block">
                    <p className="text-xs sm:text-sm font-bold text-slate-400 uppercase tracking-wider">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-6">
                {cards.map((card, idx) => (
                    <div key={idx} className={`relative overflow-hidden bg-gradient-to-br ${card.gradient} p-4 sm:p-6 rounded-2xl sm:rounded-3xl shadow-md sm:shadow-lg ${card.shadow} text-white transition-transform hover:-translate-y-0.5 duration-200`}>
                        {/* Background Icon */}
                        <i className={`fas ${card.icon} absolute -right-4 -bottom-4 sm:-right-6 sm:-bottom-6 text-7xl sm:text-9xl opacity-10 transform -rotate-12 pointer-events-none`}></i>
                        
                        <div className="relative z-10 flex flex-col h-full justify-between">
                            <div className="flex justify-between items-start mb-3 sm:mb-6">
                                <p className="text-xs sm:text-sm font-bold uppercase tracking-wider opacity-90">{card.label}</p>
                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center backdrop-blur-sm">
                                    <i className={`fas ${card.icon} text-sm sm:text-lg`}></i>
                                </div>
                            </div>
                            <p className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">{card.value}</p>
                        </div>
                    </div>
                ))}
            </div>

            <div className="p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl border border-slate-100 bg-slate-50/50 shadow-sm">
                <h3 className="text-sm sm:text-lg font-bold mb-4 text-slate-800 flex items-center gap-2">
                    <i className="fas fa-bolt text-amber-500"></i> Quick Operations
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-white p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-200 flex items-center gap-3 text-slate-700 font-bold text-xs sm:text-sm shadow-sm">
                        <div className="w-8 h-8 rounded-full bg-sky-50 flex items-center justify-center text-sky-500 shrink-0"><i className="fas fa-pills"></i></div>
                        <span>Inventory Active</span>
                    </div>
                    <div className="bg-white p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-200 flex items-center gap-3 text-slate-700 font-bold text-xs sm:text-sm shadow-sm">
                        <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 shrink-0"><i className="fas fa-truck"></i></div>
                        <span>Fast Home Delivery</span>
                    </div>
                    <div className="bg-white p-3.5 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-200 flex items-center gap-3 text-slate-700 font-bold text-xs sm:text-sm shadow-sm">
                        <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center text-purple-500 shrink-0"><i className="fas fa-shield-alt"></i></div>
                        <span>Super Admin Verified</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
